import type { Supplier, SupplierFormData } from '../types/supplier';
import api from './api';
import { cache } from '../utils/cache';
import { normalizeListResponse } from '../utils/listResponse';

type BackendSupplier = { contact_name: string; status: string; internal_notes?: string } & Supplier;

const mapSupplier = (s: BackendSupplier): Supplier => {
  const normalized = s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1).toLowerCase() : '';
  const status: Supplier['status'] = normalized === 'Inactive' ? 'Inactive' : 'Active';
  return {
    ...s,
    contactName: s.contact_name,
    status,
    internalNotes: s.internal_notes,
  };
};

export interface ListSuppliersParams {
  page?: number;
  size?: number;
  search?: string;
  status?: 'all' | 'Active' | 'Inactive';
}

export const supplierService = {
  getAll: async (opts?: { bypassCache?: boolean }): Promise<Supplier[]> => {
    const fetcher = async () => {
      const response = await api.get('/suppliers');
      return response.data.map(mapSupplier);
    };
    // During onboarding the sample supplier is seeded/cleaned mid-tour; bypass the
    // 5-min cache so a fetch that ran before the seed can't strand the picker with
    // a stale sample-less list.
    if (opts?.bypassCache) return fetcher();
    return cache.fetch('suppliers_all', fetcher);
  },

  list: async (params: ListSuppliersParams = {}): Promise<{ data: Supplier[]; total: number }> => {
    const { page = 1, size = 25, search, status } = params;
    const query = new URLSearchParams();
    query.set('limit', String(size));
    query.set('offset', String((page - 1) * size));
    query.set('include_meta', 'true');
    if (search && search.trim()) query.set('search', search.trim());
    if (status && status !== 'all') query.set('status', status.toLowerCase());
    const response = await api.get(`/suppliers?${query.toString()}`);
    const normalized = normalizeListResponse<BackendSupplier>(response.data);
    return { data: normalized.data.map(mapSupplier), total: normalized.total };
  },

  getById: async (id: string): Promise<Supplier | undefined> => {
    return cache.fetch(`suppliers_${id}`, async () => {
      try {
        const response = await api.get(`/suppliers/${id}`);
        const s = response.data;
        return {
          ...s,
          contactName: s.contact_name,
          status: s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : 'Active',
          internalNotes: s.internal_notes
        };
      } catch {
        return undefined;
      }
    });
  },

  create: async (data: SupplierFormData): Promise<Supplier> => {
    const payload = {
      name: data.name,
      code: data.code,
      contact_name: data.contactName,
      email: data.email,
      phone: data.phone,
      status: data.status.toLowerCase(),
      internal_notes: data.internalNotes
    };
    const response = await api.post('/suppliers', payload);
    cache.invalidate('suppliers');
    cache.invalidate('reports_');
    const s = response.data;
    return {
      ...s,
      contactName: s.contact_name,
      status: s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : 'Active',
      internalNotes: s.internal_notes
    };
  },

  update: async (id: string, data: SupplierFormData): Promise<Supplier> => {
    const payload = {
      name: data.name,
      code: data.code,
      contact_name: data.contactName,
      email: data.email,
      phone: data.phone,
      status: data.status.toLowerCase(),
      internal_notes: data.internalNotes
    };
    const response = await api.put(`/suppliers/${id}`, payload);
    cache.delete(`suppliers_${id}`);
    cache.delete('suppliers_all');
    cache.invalidate('reports_');
    const s = response.data;
    return {
      ...s,
      contactName: s.contact_name,
      status: s.status ? s.status.charAt(0).toUpperCase() + s.status.slice(1) : 'Active',
      internalNotes: s.internal_notes
    };
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/suppliers/${id}`);
    cache.delete(`suppliers_${id}`);
    cache.delete('suppliers_all');
    cache.invalidate('reports_');
  }
};
